
import DateFormatMixin from '@/mixins/DateFormatMixin.vue'
import { EventBus } from '@/plugins/eventBus'
import axios from 'axios'
import Vue from 'vue'
import { VueConstructor } from 'vue/types/umd'
import { DataTableHeader } from 'vuetify'


interface Comment {
  id: number
  text: string
  user_first_name: string
  user_last_name: string
  created_at: string
  updated_at: string
}

interface EditedComment {
  id: number | null
  text: string
}

export default (Vue as VueConstructor<Vue & InstanceType<typeof DateFormatMixin>>).extend({
    name: 'CommentsFormatter',
    mixins: [
      DateFormatMixin
    ],
    props: {
        item: {
            type: Object,
            required: true,
            default: () => ({})
        }
    },
    data() {
        return {
            currentPage: 1,
            dialog_comment: false,
            loading: false,
            comments: [] as Comment[],
            totalComments: 0,
            search: '',
            sortBy: ['updated_at'],
            sortDesc: [true],
            commentHeaders: [
                {
                    text: this.$vuetify.lang.t('$vuetify.comment'),
                    align: 'start',
                    value: 'text',
                    sortable: true,
                    filterable: true
                },
                {
                    text: this.$vuetify.lang.t('$vuetify.user_name'),
                    value: 'user',
                    sortable: true,
                    filterable: true
                },
                {
                    text: this.$vuetify.lang.t('$vuetify.created_at'),
                    value: 'created_at',
                    sortable: true,
                    filterable: true
                },
                {
                    text: this.$vuetify.lang.t('$vuetify.updated_at'),
                    value: 'updated_at',
                    sortable: true,
                    filterable: true
                },
                {
                    text: '',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: '50'
                }
            ] as DataTableHeader[],
            editedComment: {
                id: null,
                text: ''
            } as EditedComment,
            deleteLoading: false,
            updateLoading: false,
            itemsPerPage: 5,
            dialogConfirm: false,
            commentToDelete: null as number | null,
            
            dialogEdit: false,
        }
    },
    methods: {
        showComments() {
            this.currentPage = 1
            this.dialog_comment = true
        },
        async getComments() {
            this.loading = true
            let allComments: Comment[] = []
            let page = 1
            let hasMorePages = true

            try {
                while (hasMorePages) {
                const response = await axios.get(`/v1/workers/${this.item.id}/comments?page=${page}`)

                allComments = [...allComments, ...response.data]

                const linkHeader = response.headers.link as string | undefined
                hasMorePages = Boolean(linkHeader && linkHeader.includes('rel="next"'))
                page++
                }

                this.comments = allComments
                this.totalComments = allComments.length
            } catch (error) {
                EventBus.$emit('snackbar', { axiosError: error })
            } finally {
                this.loading = false
            }
        },
        updateSort(value: string[]) {
            this.sortBy = value
            },
        updateSortDesc(value: boolean[]) {
            this.sortDesc = value
            },
        async deleteComment(commentId: number) {
            try {
                this.deleteLoading = true
                await axios.delete(`/v1/workers/${this.item.id}/comments/${commentId}`)

                // Recharger les commentaires
                await this.refreshComments()

                EventBus.$emit('snackbar', {
                message: this.$vuetify.lang.t('$vuetify.comment_deleted_successfully'),
                color: 'success'
                })

                this.dialogConfirm = false
            } catch (error) {
                EventBus.$emit('snackbar', { axiosError: error })
            } finally {
                this.deleteLoading = false
                this.commentToDelete = null
            }
        },
        confirmDelete(commentId: number) {
            this.commentToDelete = commentId
            this.dialogConfirm = true
        },
        handleConfirmDelete() {
            if (this.commentToDelete !== null) {
                this.deleteComment(this.commentToDelete)
            }
        },
        editComment(comment: Comment) {
            this.editedComment = {
                id: comment.id,
                text: comment.text
            }
            this.dialogEdit = true
        },
        async updateComment() {
            if (!this.editedComment.text.trim()) {
                EventBus.$emit('snackbar', {
                message: this.$vuetify.lang.t('$vuetify.comment_required'),
                color: 'error'
                })
                return
            }

            try {
                this.updateLoading = true
                await axios.put(
                `/v1/workers/${this.item.id}/comments/${this.editedComment.id}`,
                { text: this.editedComment.text.trim() }
                )

                // Reload comments
                await this.refreshComments()

                EventBus.$emit('snackbar', {
                message: this.$vuetify.lang.t('$vuetify.comment_updated_successfully'),
                color: 'success'
                })

                this.dialogEdit = false
            } catch (error) {
                EventBus.$emit('snackbar', { axiosError: error })
            } finally {
                this.updateLoading = false
            }
        },
        resetEditedComment() {
            this.editedComment = {
                id: null,
                text: ''
            }
        },
        async refreshComments() {
            await this.getComments()
            // Use EventBus to reload last_comment
            EventBus.$emit('listParagonDataReload')
        },
        validateComment(v: string): boolean | string {
          if (!v) {
            return this.$vuetify.lang.t('$vuetify.comment_required');
          }
          return true;
        },
    },
    watch: {
      search() {
        this.currentPage = 1
      },
      dialog_comment(newValue: boolean) {
          if (newValue) {
              this.getComments()
          } else {
              this.comments = []
              this.totalComments = 0
              this.currentPage = 1
              this.search = ''
          }
      },
      dialogEdit(val) {
        if (!val) {
          this.resetEditedComment()
        }
      },
    },
    computed: {
        filteredComments(): Comment[] {
            if (!this.search) return this.comments

            const searchLower = this.search.toLowerCase()
            return this.comments.filter(comment => {
                const userFullName = `${comment.user_last_name} ${comment.user_first_name}`.toLowerCase()
                const text = comment.text.toLowerCase()
                const createdAt = this.localizeDate(comment.created_at).toLowerCase()
                const updatedAt = this.localizeDate(comment.updated_at).toLowerCase()

                return userFullName.includes(searchLower) ||
                    text.includes(searchLower) ||
                    createdAt.includes(searchLower) ||
                    updatedAt.includes(searchLower)
            })
        },
        paginatedComments(): Comment[] {
            let sortedComments = [...this.filteredComments]

            // Apply sorting if a column is selected
            if (this.sortBy.length > 0) {
                const sortKey = this.sortBy[0]
                const isDesc = this.sortDesc[0]

                sortedComments.sort((a: Comment, b: Comment) => {
                let aValue = a[sortKey as keyof Comment]
                let bValue = b[sortKey as keyof Comment]

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    return isDesc
                    ? bValue.localeCompare(aValue)
                    : aValue.localeCompare(bValue)
                }

                return isDesc
                    ? (bValue as number) - (aValue as number)
                    : (aValue as number) - (bValue as number)
                })
            }

            // Apply pagination
            const start = (this.currentPage - 1) * this.itemsPerPage
            const end = start + this.itemsPerPage
            return sortedComments.slice(start, end)
        },    
    }
})
